import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControlLabel,
  Typography,
  Paper,
} from "@mui/material";
import TextField from "components/common/TextField";
import Checkbox from "components/common/Checkbox";
import { DatePicker, DesktopTimePicker } from "@mui/x-date-pickers";
import { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { CalendarEvent } from "protogen/calendar_pb";

interface TaskReminderDialogProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  taskTitle?: string;
  event?: CalendarEvent | null;
  loading?: boolean;
  hasDueDateEvent?: boolean;
  defaultIsDueDate?: boolean;
  onSave: (title: string, eventDateTime: Date, isDueDate: boolean) => void;
  onDelete?: () => void;
}

const TaskReminderDialog = ({
  open,
  onClose,
  mode,
  taskTitle,
  event,
  loading = false,
  hasDueDateEvent = false,
  defaultIsDueDate = false,
  onSave,
  onDelete,
}: TaskReminderDialogProps) => {
  const [title, setTitle] = useState("");
  const [eventDate, setEventDate] = useState<Date | null>(new Date());
  const [eventTime, setEventTime] = useState<Date | null>(() => {
    const date = new Date();
    date.setHours(9, 0, 0, 0);
    return date;
  });
  const [isDueDate, setIsDueDate] = useState(defaultIsDueDate);

  useEffect(() => {
    if (open) {
      if (mode === "edit" && event) {
        setTitle(event.title || "");

        // Check if this is a due date event
        const metadata = event.meta ? JSON.parse(event.meta) : {};
        setIsDueDate(metadata.taskEventType === "dueDate");

        // Get the date from the event's startSec
        if (event.startSec) {
          try {
            const eventDateTime = new Date(Number(event.startSec) * 1000);
            setEventDate(eventDateTime);
            setEventTime(eventDateTime);
          } catch (e) {
            console.error("Error parsing event date:", e);
            const defaultDate = new Date();
            setEventDate(defaultDate);
            const defaultTime = new Date();
            defaultTime.setHours(9, 0, 0, 0);
            setEventTime(defaultTime);
          }
        } else {
          setEventDate(new Date());
          const defaultTime = new Date();
          defaultTime.setHours(9, 0, 0, 0);
          setEventTime(defaultTime);
        }
      } else {
        setTitle(defaultIsDueDate ? "Due date" : "");
        setEventDate(new Date());
        const defaultTime = new Date();
        defaultTime.setHours(9, 0, 0, 0);
        setEventTime(defaultTime);
        setIsDueDate(defaultIsDueDate);
      }
    }
  }, [open, mode, event, defaultIsDueDate]);

  const handleSubmit = async () => {
    if (!eventDate || !eventTime) return;

    const eventDateTime = new Date(eventDate);
    eventDateTime.setHours(eventTime.getHours());
    eventDateTime.setMinutes(eventTime.getMinutes());

    onSave(title, eventDateTime, isDueDate);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          style: {
            background: "rgba(209, 209, 209, 0.30)",
            backdropFilter: "blur(3px)",
          },
        },
      }}
      PaperComponent={Paper}
      PaperProps={{
        style: {
          borderRadius: "12px",
          backgroundColor: "#FFF",
          boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
          border: "1px solid #EAECF0",
        },
      }}
    >
      <DialogTitle>
        {mode === "create" ? "Add task reminder" : "Edit task reminder"}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            pt: "16px",
          }}
        >
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={mode === "create" ? taskTitle : undefined}
            fullWidth
          />

          {!hasDueDateEvent && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDueDate}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsDueDate(checked);
                      if (checked && !title) {
                        setTitle("Due date");
                      } else if (!checked && title === "Due date") {
                        setTitle("");
                      }
                    }}
                  />
                }
                label="This is a due date"
              />
              <Typography
                variant="body"
                color="text.secondary"
                sx={{ marginLeft: "32px", marginTop: "-4px" }}
              >
                Due dates appear in the task list and are visible to clients.
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: "16px", marginTop: "16px" }}>
            <DatePicker
              label="Date"
              value={eventDate}
              onChange={setEventDate}
              sx={{
                flex: 1,
                ".MuiButtonBase-root": { marginRight: "unset" },
              }}
            />
            <DesktopTimePicker
              label="Time"
              value={eventTime}
              onChange={setEventTime}
              sx={{
                flex: 1,
                ".MuiButtonBase-root": { marginRight: "unset" },
              }}
            />
          </Box>

          <Typography variant="body" color="text.secondary">
            You'll receive a notification at the time of the reminder.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          padding: "24px",
          justifyContent:
            mode === "edit" && onDelete ? "space-between" : "flex-end",
        }}
      >
        {mode === "edit" && onDelete && (
          <Button
            onClick={() => {
              onDelete();
              onClose();
            }}
            disabled={loading}
            variant="text"
            color="error"
            startIcon={<Trash2 size={16} />}
            sx={{ "&:hover": { backgroundColor: "transparent" } }}
          >
            Delete
          </Button>
        )}
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Button onClick={onClose} disabled={loading} variant="text">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !eventDate || !eventTime}
          >
            {loading
              ? "Saving..."
              : mode === "create"
                ? "Add reminder"
                : "Update"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TaskReminderDialog;
